import { getApiBaseUrl } from '../../lib/api';
/**
 * Listing Compliance Scanner
 * 
 * Scans property listings for RRA 2025 compliance issues:
 * - Rent bidding language (banned)
 * - Discriminatory phrases (No DSS, No children, etc.)
 * - Rent shown as range instead of single figure
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, ShieldCheck, AlertTriangle, XCircle, CheckCircle,
  Home, FileText
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { toast } from 'sonner';

const API_URL = getApiBaseUrl();

const ListingScannerPage = () => {
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [form, setForm] = useState({
    propertyId: '',
    listingText: '',
    advertisedRent: '',
  });

  const handleScan = async () => {
    if (!form.listingText.trim()) {
      toast.error('Please enter listing text to scan');
      return;
    }

    const token = localStorage.getItem('token');
    
    try {
      setScanning(true);
      setScanResult(null);
      
      const response = await fetch(`${API_URL}/api/rra-compliance/listings/scan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          property_id: form.propertyId || '00000000-0000-0000-0000-000000000000',
          listing_text: form.listingText,
          advertised_rent: form.advertisedRent || null,
        }),
      });
      
      if (response.ok) {
        const result = await response.json();
        setScanResult(result);
        
        if (result.status === 'compliant') {
          toast.success('No compliance issues found!');
        } else {
          toast.error('Issues found - please review below');
        }
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Scan failed');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setScanning(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'compliant':
        return <CheckCircle className="h-6 w-6 text-green-500" />;
      case 'issues_found':
        return <AlertTriangle className="h-6 w-6 text-amber-500" />;
      case 'blocked':
        return <XCircle className="h-6 w-6 text-red-500" />;
      default:
        return <ShieldCheck className="h-6 w-6 text-muted-foreground" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'compliant':
        return { text: 'Compliant', color: 'text-green-600', bg: 'bg-green-50' };
      case 'issues_found':
        return { text: 'Issues Found', color: 'text-amber-600', bg: 'bg-amber-50' };
      case 'blocked':
        return { text: 'Blocked', color: 'text-red-600', bg: 'bg-red-50' };
      default:
        return { text: 'Unknown', color: 'text-gray-600', bg: 'bg-gray-50' };
    }
  };

  return (
    <div className="space-y-6 p-6" data-testid="listing-scanner-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Search className="h-6 w-6 text-primary" />
            Listing Compliance Scanner
          </h1>
          <p className="text-muted-foreground">
            Check your listing text for RRA 2025 compliance issues
          </p>
        </div>
        <Link to="/compliance">
          <Button variant="outline" data-testid="back-to-dashboard-btn">
            Back to Dashboard
          </Button>
        </Link>
      </div>

      {/* Info */}
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>What we check for</AlertTitle>
        <AlertDescription>
          <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
            <li><strong>Rent bidding:</strong> Phrases like &quot;offers over&quot;, &quot;best offer&quot; are banned</li>
            <li><strong>Discriminatory language:</strong> &quot;No DSS&quot;, &quot;No children&quot;, &quot;professionals only&quot; etc.</li>
            <li><strong>Rent format:</strong> Must be a single figure, not a range like &quot;£1000-1200&quot;</li>
          </ul>
        </AlertDescription>
      </Alert>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Input Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Listing Details</CardTitle>
            <CardDescription>
              Paste your listing text to check for compliance
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="listing_text">Listing Text *</Label>
              <Textarea
                id="listing_text"
                placeholder="Paste your full listing description here..."
                value={form.listingText}
                onChange={(e) => setForm({ ...form, listingText: e.target.value })}
                className="min-h-[200px]"
                data-testid="listing-text-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="advertised_rent">Advertised Rent (optional)</Label>
              <Input
                id="advertised_rent"
                placeholder="e.g., £1,500 pcm"
                value={form.advertisedRent}
                onChange={(e) => setForm({ ...form, advertisedRent: e.target.value })}
                data-testid="rent-input"
              />
              <p className="text-xs text-muted-foreground">
                Enter exactly as shown in your listing to check format compliance
              </p>
            </div>
          </CardContent>
          <CardFooter>
            <Button 
              onClick={handleScan} 
              disabled={scanning || !form.listingText.trim()}
              className="w-full"
              data-testid="scan-btn"
            >
              {scanning ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Scanning...
                </>
              ) : (
                <>
                  <Search className="h-4 w-4 mr-2" />
                  Scan Listing
                </>
              )}
            </Button>
          </CardFooter>
        </Card>

        {/* Results */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Scan Results</CardTitle>
            <CardDescription>
              Compliance status and any issues found
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!scanResult ? (
              <div className="text-center py-12 text-muted-foreground">
                <ShieldCheck className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Enter listing text and click scan to check compliance</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="scan-results">
                {/* Status Banner */}
                <div className={`rounded-lg p-4 ${getStatusText(scanResult.status).bg}`}>
                  <div className="flex items-center gap-3">
                    {getStatusIcon(scanResult.status)}
                    <div>
                      <p className={`font-semibold ${getStatusText(scanResult.status).color}`}>
                        {getStatusText(scanResult.status).text}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Scanned at {new Date(scanResult.scanned_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Issues */}
                {scanResult.scan_details?.issues?.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-medium">Issues Found:</h4>
                    {scanResult.scan_details.issues.map((issue, idx) => (
                      <Alert key={idx} variant={issue.severity === 'high' ? 'destructive' : 'default'}>
                        <AlertTriangle className="h-4 w-4" />
                        <AlertTitle className="capitalize">{issue.type.replace(/_/g, ' ')}</AlertTitle>
                        <AlertDescription>
                          {issue.phrases && (
                            <p className="text-sm mt-1">
                              <strong>Found:</strong> {issue.phrases.join(', ')}
                            </p>
                          )}
                          <p className="text-sm mt-1">
                            <strong>Action:</strong> {issue.action_required}
                          </p>
                        </AlertDescription>
                      </Alert>
                    ))}
                  </div>
                )}

                {/* Specific Findings */}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between py-2 border-b">
                    <span>Rent Bidding Language</span>
                    {scanResult.rent_bidding_detected ? (
                      <Badge variant="destructive">Detected</Badge>
                    ) : (
                      <Badge variant="outline">Clear</Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between py-2 border-b">
                    <span>Discriminatory Language</span>
                    {scanResult.discriminatory_language_detected ? (
                      <Badge variant="destructive">Detected</Badge>
                    ) : (
                      <Badge variant="outline">Clear</Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between py-2 border-b">
                    <span>Rent Range Format</span>
                    {scanResult.scan_details?.rent_range_detected ? (
                      <Badge variant="warning">Range Detected</Badge>
                    ) : (
                      <Badge variant="outline">OK</Badge>
                    )}
                  </div>
                </div>

                {/* Detected Rent */}
                {scanResult.advertised_rent_amount && (
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm">
                      <strong>Detected Rent:</strong> £{scanResult.advertised_rent_amount.toLocaleString()} pcm
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Banned Phrases Reference */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Reference: Banned Phrases</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <h4 className="font-medium text-red-600 mb-2">Rent Bidding (Banned)</h4>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>• Offers over</li>
                <li>• Best offer</li>
                <li>• Highest offer</li>
                <li>• Rent negotiable upwards</li>
                <li>• Starting rent</li>
                <li>• Guide rent</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-red-600 mb-2">Benefits Discrimination</h4>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>• No DSS</li>
                <li>• No housing benefit</li>
                <li>• No Universal Credit</li>
                <li>• Working only</li>
                <li>• Professionals only</li>
                <li>• No LHA</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-red-600 mb-2">Other Discrimination</h4>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>• No children</li>
                <li>• No families</li>
                <li>• Adults only</li>
                <li>• Couples only</li>
                <li>• No pets (must consider requests)</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ListingScannerPage;
